import { useEffect, useMemo, useState } from 'react';
import { customerFormInitialValues } from '../data/customerConstants';
import {
  createCustomer,
  deleteCustomer,
  fetchCustomers,
  updateCustomer,
} from '../services/customerService';

const CUSTOMER_ACTIVE_VIEW_STORAGE_KEY = 'seaweeds.customerActiveView';

function useCustomers() {
  const [activeView, setActiveView] = useState(getInitialCustomerActiveView);
  const [filterValues, setFilterValues] = useState({
    customerName: '',
    date: '',
    status: 'All',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    customerName: '',
    date: '',
    status: 'All',
  });
  const [formValues, setFormValues] = useState(customerFormInitialValues);
  const [formErrors, setFormErrors] = useState({});
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editValues, setEditValues] = useState(customerFormInitialValues);
  const [editErrors, setEditErrors] = useState({});
  const [editError, setEditError] = useState('');
  const [isEditSaving, setIsEditSaving] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(CUSTOMER_ACTIVE_VIEW_STORAGE_KEY, activeView);
  }, [activeView]);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage('');
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [successMessage]);

  const filteredCustomers = useMemo(() => {
    const normalizedSearch = appliedFilters.customerName.trim().toLowerCase();

    return customers.filter((customer) => {
      if (customer.status === 'Deleted') {
        return false;
      }

      const matchesSearch =
        !normalizedSearch ||
        customer.companyName.toLowerCase().includes(normalizedSearch);

      const matchesStatus =
        appliedFilters.status === 'All' || customer.status === appliedFilters.status;

      const matchesDate =
        !appliedFilters.date ||
        toDateInputValue(customer.createdAt) === appliedFilters.date;

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [appliedFilters, customers]);
  const customerSearchSuggestions = useMemo(
    () => buildCustomerSearchSuggestions(customers),
    [customers]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadCustomers() {
      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchCustomers();

        if (isMounted) {
          setCustomers(response);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadCustomers();

    return () => {
      isMounted = false;
    };
  }, []);

  function handleFormChange(event) {
    const { name, value, files } = event.target;

    if (files?.[0]) {
      handleFileChange(files[0], setFormValues, setFormErrors, setSubmitError);
      return;
    }

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    setFormErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;
    const nextValues = {
      ...filterValues,
      [name]: value,
    };

    setFilterValues(nextValues);
    setAppliedFilters(nextValues);
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filterValues);
  }

  function handleFilterReset() {
    const resetValues = {
      customerName: '',
      date: '',
      status: 'All',
    };

    setFilterValues(resetValues);
    setAppliedFilters(resetValues);
  }

  function clearFormPhoto() {
    setFormValues((currentValues) => ({
      ...currentValues,
      photoUrl: '',
    }));
    setFormErrors((currentErrors) => ({
      ...currentErrors,
      photoUrl: '',
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateCustomerForm(formValues);

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const createdCustomer = await createCustomer({
        companyName: formValues.companyName.trim(),
        branchLocations: normalizeBranchLocations(formValues.branchLocations),
        contactNumber: formValues.contactNumber.trim(),
        photoUrl: formValues.photoUrl,
        status: formValues.status,
      });

      setCustomers((currentCustomers) => [...currentCustomers, createdCustomer]);
      resetForm();
      setActiveView('list');
      setSuccessMessage('Customer added successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  function openEditModal(customer) {
    setEditingCustomer(customer);
    setEditValues({
      companyName: customer.companyName,
      contactNumber: customer.contactNumber || '',
      branchLocations: normalizeBranchLocations(customer.branchLocations),
      photoUrl: customer.photoUrl || '',
      status: customer.status,
    });
    setEditErrors({});
    setEditError('');
    setSuccessMessage('');
  }

  function closeEditModal() {
    setEditingCustomer(null);
    setEditValues(customerFormInitialValues);
    setEditErrors({});
    setEditError('');
  }

  function handleEditChange(event) {
    const { name, value, files } = event.target;

    if (files?.[0]) {
      handleFileChange(files[0], setEditValues, setEditErrors, setEditError);
      return;
    }

    setEditValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    setEditErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
    }));
    setEditError('');
    setSuccessMessage('');
  }

  function clearEditPhoto() {
    setEditValues((currentValues) => ({
      ...currentValues,
      photoUrl: '',
    }));
    setEditErrors((currentErrors) => ({
      ...currentErrors,
      photoUrl: '',
    }));
    setEditError('');
    setSuccessMessage('');
  }

  async function handleEditSubmit(event) {
    event.preventDefault();

    if (!editingCustomer) {
      return false;
    }

    const nextErrors = validateCustomerForm(editValues);

    if (Object.keys(nextErrors).length) {
      setEditErrors(nextErrors);
      return false;
    }

    setIsEditSaving(true);
    setEditError('');

    try {
      const updatedCustomer = await updateCustomer(editingCustomer.id, {
        companyName: editValues.companyName.trim(),
        branchLocations: normalizeBranchLocations(editValues.branchLocations),
        contactNumber: editValues.contactNumber.trim(),
        photoUrl: editValues.photoUrl,
        status: editValues.status,
      });

      setCustomers((currentCustomers) =>
        currentCustomers.map((customer) =>
          customer.id === editingCustomer.id ? updatedCustomer : customer
        )
      );
      closeEditModal();
      setSuccessMessage('Customer updated successfully.');
      return true;
    } catch (error) {
      setEditError(error.message);
      return false;
    } finally {
      setIsEditSaving(false);
    }
  }

  async function handleDelete(customer) {
    setIsSaving(true);
    setSubmitError('');

    try {
      await deleteCustomer(customer.id);
      setCustomers((currentCustomers) =>
        currentCustomers.map((currentCustomer) =>
          currentCustomer.id === customer.id
            ? { ...currentCustomer, status: 'Deleted' }
            : currentCustomer
        )
      );
      setSuccessMessage('Customer deleted successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  function resetForm() {
    setFormValues(customerFormInitialValues);
    setFormErrors({});
    setSubmitError('');
  }

  function handleCancel() {
    resetForm();
    setActiveView('list');
  }

  return {
    activeView,
    filterValues,
    formValues,
    formErrors,
    filteredCustomers,
    customerSearchSuggestions,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    editingCustomer,
    editValues,
    editErrors,
    editError,
    isEditSaving,
    setActiveView,
    clearFormPhoto,
    handleFormChange,
    handleFilterChange,
    handleFilterSubmit,
    handleFilterReset,
    handleSubmit,
    handleCancel,
    openEditModal,
    closeEditModal,
    clearEditPhoto,
    handleEditChange,
    handleEditSubmit,
    handleDelete,
  };
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  return new Date(value).toISOString().slice(0, 10);
}

function getInitialCustomerActiveView() {
  const storedValue = window.localStorage.getItem(CUSTOMER_ACTIVE_VIEW_STORAGE_KEY);

  if (storedValue === 'list' || storedValue === 'create') {
    return storedValue;
  }

  return 'list';
}

function handleFileChange(file, setValues, setErrors, setErrorMessage) {
  const reader = new FileReader();

  reader.onload = () => {
    setValues((currentValues) => ({
      ...currentValues,
      photoUrl: typeof reader.result === 'string' ? reader.result : '',
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      photoUrl: '',
    }));
    setErrorMessage('');
  };

  reader.onerror = () => {
    setErrorMessage('Unable to read the selected photo right now.');
  };

  reader.readAsDataURL(file);
}

function validateCustomerForm(values) {
  const errors = {};

  if (!values.companyName.trim()) {
    errors.companyName = 'Company name is required.';
  }

  if (!normalizeBranchLocations(values.branchLocations).branches.length) {
    errors.branchLocations = 'At least one branch location is required.';
  }

  if (!values.status.trim()) {
    errors.status = 'Status is required.';
  }

  return errors;
}

function normalizeBranchLocations(value) {
  const branches = Array.isArray(value?.branches)
    ? value.branches.map((branch) => String(branch || '').trim()).filter(Boolean)
    : [];
  const dedupedBranches = Array.from(new Set(branches));
  const primaryBranchLocation = dedupedBranches.includes(value?.primaryBranchLocation)
    ? value.primaryBranchLocation
    : dedupedBranches[0] || '';

  return {
    branches: dedupedBranches,
    primaryBranchLocation,
  };
}

function buildCustomerSearchSuggestions(customers) {
  const suggestionsByKey = new Map();

  customers.forEach((customer) => {
    if (customer.status === 'Deleted') {
      return;
    }

    const companyName = String(customer.companyName || '').trim();
    if (companyName) {
      suggestionsByKey.set(`company:${companyName.toLowerCase()}`, {
        group: 'customer-company',
        value: companyName,
        label: companyName,
        detail: 'Company',
      });
    }
  });

  return Array.from(suggestionsByKey.values()).sort((firstSuggestion, secondSuggestion) =>
    firstSuggestion.label.localeCompare(secondSuggestion.label)
  );
}

export default useCustomers;
